import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  api, ApiError, type CallQuestion, type IncidentDetail, type ListItem, type Member,
} from '../lib/api'
import { QuestionFields, isMultiple, mergeTrees, picked } from '../lib/questions'
import { decodeVehicle } from '../lib/vehicles'
import { given } from './CallAnswers'
import { PRIORITIES } from './Intake'
import { ClockField, anchorOf, callInIso, clockOf, placeTime } from './LoggedCall'
import { MemberPicker } from './MemberPicker'
import { NoteEditor } from './CallNotes'
import { PhoneInput } from './PhoneInput'

/** CORRECT A CALL, OPEN OR CLOSED -- THE WHOLE OF IT (078, 079), for the role
 *  ticked "Correct a call".
 *
 *  The customer: "we need to be able to edit any call that's closed already.
 *  The timing, everything ... It should be a note that somebody adjusted the
 *  time", then "edit the whole call from top to bottom, not only the timing,
 *  everything, nature, name" -- "a closed call or an ongoing call". So ONLY
 *  WHAT IS EDITED IS SENT, with one reason, and the server keeps what each
 *  value was (`incident_corrections`). What came in from the phone system --
 *  the number, and the name with it, taken from the ringing caller-ID card --
 *  "is how the call came in" and is shown, never offered. What the system did
 *  (the broadcast, every message sent, the history, the call number) is not
 *  here at all. `correct-whole-call-mockup.html`.
 *
 *  THE CALL'S ACCEPTED AND ON-SCENE TIMES ARE ITS MEMBERS': the first to take
 *  it and the first to arrive. While any member carries one, the call-level
 *  line shows it and is changed on his line; the server keeps the same rule.
 *
 *  A time box counts as edited when its hours and minutes differ from the old
 *  value's -- the seconds a box cannot show never make a correction. */

type Box = { orig: string | null; text: string }
const boxOf = (iso: string | null): Box => ({ orig: iso, text: clockOf(iso) })
const dirty = (b: Box) => b.text !== clockOf(b.orig)

/** An instant moved by whole days on this screen's clock. */
function shifted(iso: string, days: number): string {
  const d = new Date(iso)
  d.setDate(d.getDate() + days)
  return d.toISOString()
}
/** Whole days from one 'YYYY-MM-DD' to another. */
function daysBetween(from: string, to: string): number {
  const a = new Date(`${from}T12:00`), b = new Date(`${to}T12:00`)
  return Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())
    ? 0 : Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

function dayOf(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** The earliest of some instants -- compared as times, never as text: the
 *  server's and this screen's ISO strings are written differently. */
function earliest(times: (string | null)[]): string | null {
  return times.filter((t): t is string => !!t)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] ?? null
}
const same = (a: string | null, b: string | null) =>
  !!a && !!b && new Date(a).getTime() === new Date(b).getTime()

/** A box's words as they compare: spaces collapsed, ends trimmed. */
const norm = (v: string | null | undefined) => (v ?? '').replace(/\s+/g, ' ').trim()

const URGENCY = Object.fromEntries(PRIORITIES.map((p) => [p.v, p.label])) as Record<number, string>
const STAYING: Record<string, string> = { true: 'Staying', false: 'Not staying', null: 'Not said' }

/** The words of the call, as the form holds them. */
type Words = {
  caller_name: string; caller_phone: string
  relationship_id: string; relationship_label: string
  caller_on_scene: boolean | null; caller_urgency: number | null; anonymous: boolean
  priority: number
  address: string; location_details: string
  cross_street_a: string; cross_street_b: string; location_note: string
  resolution_id: string; resolution_label: string; outcome_note: string
}

function wordsOf(call: IncidentDetail): Words {
  const loc = call.locations.at(-1)
  // THE ADDRESS IS THE LABEL, unless the label is only the details (071).
  const address = loc && !loc.place_id && loc.details && loc.label === loc.details ? '' : (loc?.label ?? '')
  return {
    caller_name: call.caller_name ?? '', caller_phone: call.caller_phone ?? '',
    relationship_id: call.relationship_id ?? '', relationship_label: call.relationship_label ?? '',
    caller_on_scene: call.caller_on_scene, caller_urgency: call.caller_urgency,
    anonymous: !!call.anonymous, priority: call.priority,
    address, location_details: loc?.details ?? '',
    cross_street_a: loc?.cross_street_a ?? '', cross_street_b: loc?.cross_street_b ?? '',
    location_note: loc?.note ?? '',
    resolution_id: '', resolution_label: call.resolution_label ?? '', outcome_note: call.outcome_note ?? '',
  }
}

/** One box of the form, amber with what it was once it changes. */
function Row({ label, changed, was, locked, children }: {
  label: string; changed: boolean; was: string; locked?: string; children: ReactNode
}) {
  return (
    <div className={`field ccor-f ${changed ? 'chg' : ''}`}>
      <label>
        {label}
        {changed && <span className="ccor-wasnote">corrected · was “{was || 'none'}”</span>}
        {locked && <span className="ccor-lock">{locked}</span>}
      </label>
      {children}
    </div>
  )
}

export type Corrected = { changes: number; where_changed?: boolean; going?: number }

/** Whether two answers say the same: several picks in any order, a vehicle
 *  written in another shape, a typed answer with other spaces (079 review). */
function sameAnswer(q: CallQuestion, a: string, b: string): boolean {
  if (q.kind === 'vehicle') return JSON.stringify(decodeVehicle(a)) === JSON.stringify(decodeVehicle(b))
  if (q.kind === 'choice' || isMultiple(q)) return picked(q, a).join('\u0000') === picked(q, b).join('\u0000')
  return norm(a) === norm(b)
}

export function CorrectCall({ call: live, onSaved, onCancel }: {
  call: IncidentDetail
  onSaved: (result: Corrected) => void
  onCancel: () => void
}) {
  /** THE CALL AS IT WAS WHEN THE FORM OPENED (079 review). An open call is
   *  re-read after every press on it; read live, what the form compares with
   *  would move under it, and what it sends back is checked by the server
   *  against this same moment (`fingerprint`). */
  const [call] = useState(live)
  const closedCall = call.status === 'closed'
  const [roster, setRoster] = useState<Member[]>([])
  const [natureList, setNatureList] = useState<ListItem[]>([])
  const [relationships, setRelationships] = useState<ListItem[]>([])
  const [resolutions, setResolutions] = useState<ListItem[]>([])
  useEffect(() => {
    api.members().then(setRoster).catch(() => {})
    api.settingsList('natures').then(setNatureList).catch(() => {})
    api.settingsList('relationships').then(setRelationships).catch(() => {})
    api.settingsList('resolutions').then(setResolutions).catch(() => {})
  }, [])

  // ---- the words ------------------------------------------------------------------
  const [start] = useState(() => wordsOf(call))
  const [w, setW] = useState<Words>(start)
  const set = (patch: Partial<Words>) => setW((cur) => ({ ...cur, ...patch }))
  const differs = (k: keyof Words) => (typeof start[k] === 'string'
    ? norm(w[k] as string) !== norm(start[k] as string)
    : w[k] !== start[k])
  const relChanged = norm(w.relationship_label) !== norm(start.relationship_label)
  const resChanged = norm(w.resolution_label) !== norm(start.resolution_label)
  const nameLocked = !!call.caller_name_from_phone
  const phoneLocked = !!call.caller_phone_from_phone

  // ---- the other numbers: corrected or added, never taken away ----------------------
  const [nums, setNums] = useState(() => (call.callback_numbers ?? []).map((n) => ({
    id: n.id, label: n.label ?? '', phone: n.phone_raw,
  })))
  const [newNums, setNewNums] = useState<{ label: string; phone: string }[]>([])
  const numChanges = nums.filter((n) => {
    const was = (call.callback_numbers ?? []).find((o) => o.id === n.id)
    return was && (norm(n.label) !== norm(was.label) || norm(n.phone) !== norm(was.phone_raw))
  })
  const numAdds = newNums.filter((n) => n.phone.trim())

  // ---- the notes (081): a typo fixed, what it said kept by the server ----------------
  const callNotes = call.call_notes ?? []
  const [noteEdits, setNoteEdits] = useState<Record<number, string>>(
    () => Object.fromEntries(callNotes.map((n) => [n.id, n.body])))
  const noteChanges = callNotes.filter((n) => (noteEdits[n.id] ?? n.body).trim() !== n.body.trim())

  // ---- the natures --------------------------------------------------------------------
  const callNatures = (call.natures && call.natures.length
    ? call.natures : [{ position: 0, nature_id: call.nature_id ?? null, nature_label: call.nature_label }])
  const [firstId, setFirstId] = useState<string>(callNatures[0]?.nature_id ?? '')
  const [extras, setExtras] = useState(() => callNatures.slice(1).map((n) => ({
    position: n.position as number | null, nature_id: n.nature_id ?? '', label: n.nature_label, removed: false,
  })))
  const liveNatures = natureList.filter((n) => !n.retired_at)
  const natureIds = [firstId, ...extras.filter((e) => !e.removed).map((e) => e.nature_id)].filter(Boolean)
  const callNatureIds = callNatures.map((n) => n.nature_id ?? '').filter(Boolean)
  const naturesChanged = natureIds.join() !== callNatureIds.join()
  const [trees, setTrees] = useState<Record<string, CallQuestion[]>>({})
  useEffect(() => {
    if (!naturesChanged) return
    for (const id of natureIds) {
      if (trees[id]) continue
      api.questionTree(id)
        .then((r) => setTrees((t) => ({ ...t, [id]: r.questions })))
        .catch(() => setTrees((t) => ({ ...t, [id]: [] })))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [natureIds.join()])
  /** THE QUESTIONS THE CORRECTED NATURES ASK, merged as the server merges
   *  them; the call's own while its natures are unchanged. */
  // ONE ARRAY PER CHANGE, NOT PER DRAW (079 review): a new array every render
  // re-ran the effect below, which set the answers, which drew again -- for ever.
  const treeKey = natureIds.join()
  const tree: CallQuestion[] | null = useMemo(
    () => (!naturesChanged ? call.questions
      : natureIds.every((id) => trees[id]) ? mergeTrees(natureIds.map((id) => trees[id])) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [naturesChanged, treeKey, trees, call.questions])
  const sectionNames = natureIds.map((id) => liveNatures.find((n) => n.id === id)?.name ?? '')

  // ---- the answers ---------------------------------------------------------------------
  const [answers, setAnswers] = useState<Record<string, string>>(() => given(call.answers, call.questions))
  const startAnswers = useMemo(() => (tree ? given(call.answers, tree) : {}), [tree, call.answers])
  useEffect(() => {
    // A NEW NATURE'S QUESTIONS start from what the call already holds -- and
    // nothing is set when nothing is new, or this would draw for ever.
    if (!tree) return
    const seed = given(call.answers, tree)
    setAnswers((cur) => (Object.keys(seed).some((k) => !(k in cur)) ? { ...seed, ...cur } : cur))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree])
  const answerChanges = (tree ?? []).filter(
    (q) => !sameAnswer(q, answers[q.key] ?? '', startAnswers[q.key] ?? ''))

  // ---- the times and who went (078) ------------------------------------------------------
  // A CAME-IN OR CLOSED TIME NOT KNOWN (083): the column only puts the call
  // on its day, so its box starts EMPTY -- the placeholder is no time anybody
  // gave -- and stays not known until somebody types one.
  const calledUnknown = !!call.called_at_unknown
  const closedUnknown = !!call.closed_at_unknown
  const startTime = calledUnknown ? '' : clockOf(call.called_at)
  // THE DAY, ON THE ORGANIZATION'S CLOCK when the time is not known (083
  // review): the stand-in is that day's start there, which this screen's own
  // clock may put on the day before.
  const startDate = (calledUnknown && call.called_day) || dayOf(call.called_at)
  const [date, setDate] = useState(startDate)
  const [time, setTime] = useState(startTime)
  const [accepted, setAccepted] = useState(boxOf(call.accepted_at))
  const [onScene, setOnScene] = useState(boxOf(call.on_scene_at))
  const [covered, setCovered] = useState(boxOf(call.covered_at))
  const [closed, setClosed] = useState(boxOf(closedUnknown ? null : call.closed_at))
  // TAKEN OFF ALREADY (080) is shown crossed out and is not corrected; `off`
  // is a member taken off on THIS form, with undo until it is saved.
  const gone = call.responders.filter((r) => r.removed_at)
  const [rows, setRows] = useState(() => call.responders.filter((r) => !r.removed_at).map((r) => ({
    id: r.id, member_id: r.member_id, who: r.who, role: r.role, released: !!r.released_at,
    took: boxOf(r.accepted_at), arrived: boxOf(r.on_scene_at), off: false,
  })))
  const onCall = rows.filter((r) => !r.off)
  const [added, setAdded] = useState<
    { id: string; who: string; role: 'primary' | 'backup'; took: string; arrived: string }[]>([])
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const callInMoved = date !== startDate || time !== startTime
  /** Another day, the time still not known (083): sent as the day alone. */
  const dayOnly = calledUnknown && !time
  /** A CALL LOGGED ON THE WRONG DAY MOVES WHOLE: when the day it came in
   *  changes, every time nobody retyped moves by the same days with it. */
  const shift = callInMoved ? daysBetween(startDate, date) : 0
  /** Where a time typed here is placed: after the time it came in -- or, not
   *  known, as Log afterwards places them, after the first of the times the
   *  boxes hold on the day it came in (`anchorOf`). */
  const base = dayOnly
    ? anchorOf(date, [
      ...rows.filter((r) => !r.off).flatMap((r) => [r.took.text, r.arrived.text]),
      ...added.flatMap((a) => [a.took, a.arrived]),
      accepted.text, onScene.text, covered.text, ...(closedCall ? [closed.text] : []),
    ])
    : callInMoved ? callInIso(date, time) : call.called_at
  const effective = (b: Box, at: string | null) =>
    (dirty(b) ? (b.text ? placeTime(b.text, at) : null)
      : b.orig ? (shift ? shifted(b.orig, shift) : b.orig) : null)
  const changed = (b: Box) => dirty(b) || (shift !== 0 && !!b.orig)
  const crewAccepted = earliest([
    ...onCall.map((r) => effective(r.took, base)),
    ...added.map((a) => (a.took ? placeTime(a.took, base) : null)),
  ])
  const crewArrived = earliest([
    ...onCall.map((r) => effective(r.arrived, base)),
    ...added.map((a) => (a.arrived ? placeTime(a.arrived, base) : null)),
  ])
  const crewChanged = onCall.some((r) => changed(r.took) || changed(r.arrived)) || added.length > 0
    || rows.some((r) => r.off)
  const arrivalGone = !crewArrived && crewChanged
    && [...rows.map((r) => r.arrived.orig), ...gone.map((r) => r.on_scene_at)]
      .some((t) => same(t, call.on_scene_at))
  /** NOBODY LEFT GOING ON AN OPEN CALL (080): back to waiting, with no
   *  accepted time -- the server's rule. A member stood down after a move is
   *  not going. */
  const nobodyGoing = !closedCall && rows.some((r) => r.off) && added.length === 0
    && onCall.every((r) => r.released)
  /** THE CALL'S ACCEPTED TIME WAS A MEMBER'S, AND NOBODY IS LEFT TO HAVE ONE
   *  (080): it goes with him, as the server takes it. */
  const acceptedGone = nobodyGoing || (!crewAccepted && crewChanged
    && [...rows.map((r) => r.took.orig), ...gone.map((r) => r.accepted_at)]
      .some((t) => same(t, call.accepted_at)))
  const ownOf = (iso: string | null, crewTimes: (string | null)[]) =>
    (iso && !crewTimes.some((t) => same(t, iso)) ? (shift ? shifted(iso, shift) : iso) : null)
  // A MEMBER'S TIME IS NO "OWN" TIME OF THE CALL, taken off or not -- the
  // server's rule (`corrections.correct`'s `before`).
  const firstAccepted = crewAccepted
    && earliest([crewAccepted, ownOf(call.accepted_at,
      [...rows.map((r) => r.took.orig), ...gone.map((r) => r.accepted_at)])])
  const firstArrived = crewArrived
    && earliest([crewArrived, ownOf(call.on_scene_at,
      [...rows.map((r) => r.arrived.orig), ...gone.map((r) => r.on_scene_at)])])
  const hasPrimary = onCall.some((r) => r.role === 'primary') || added.some((a) => a.role === 'primary')
  /** Primaries going after this save -- more than one is refused (review). */
  const primaries = onCall.filter((r) => r.role === 'primary' && !r.released).length
    + added.filter((a) => a.role === 'primary').length
  /** THE PRIMARY TAKEN OFF, A BACKUP GOING: the backup becomes the primary
   *  (080), as on the open call -- said here before it is saved. */
  const promoted = rows.some((r) => r.off && r.role === 'primary' && !r.released) && !hasPrimary
    ? onCall.filter((r) => r.role === 'backup' && !r.released)
        .sort((a, b) => (a.took.orig ?? '').localeCompare(b.took.orig ?? ''))[0] ?? null
    : null
  const timeCount = (callInMoved ? 1 : 0) + [accepted, onScene, covered, closed].filter(dirty).length
    + onCall.filter((r) => changed(r.took) || changed(r.arrived)).length + added.length
    + rows.filter((r) => r.off).length

  // ---- what changed, by section ----------------------------------------------------------
  const callerCount = (['caller_name', 'caller_phone', 'caller_on_scene', 'caller_urgency', 'anonymous'] as const)
    .filter(differs).length + (relChanged ? 1 : 0) + numChanges.length + numAdds.length
    + noteChanges.length
  const whereCount = (['address', 'location_details', 'location_note'] as const).filter(differs).length
    + (differs('cross_street_a') || differs('cross_street_b') ? 1 : 0)
  const whatCount = (naturesChanged ? 1 : 0) + (differs('priority') ? 1 : 0) + answerChanges.length
  const endCount = closedCall ? (resChanged ? 1 : 0) + (differs('outcome_note') ? 1 : 0) : 0
  const total = callerCount + whereCount + whatCount + timeCount + endCount
  const needs = total === 0 ? 'Nothing changed yet'
    : naturesChanged && !tree ? 'Loading the new nature’s questions…'
      : !base ? 'Needs the time it came in'
        : closedCall && !closedUnknown && !closed.text ? 'A closed call always closed at some time — say when, rather than clearing it'
          // A MEMBER'S TIME MAY BE NOT KNOWN (083); one recorded is corrected,
          // never emptied -- the server's rule.
          : onCall.some((r) => r.took.orig && !r.took.text) ? 'A time a member took it is corrected, never emptied'
              : !norm(w.address) && !norm(w.location_details) ? 'A call needs an address or location details'
                : numChanges.some((n) => !n.phone.trim()) ? 'Another number is corrected, never emptied'
                  : noteChanges.some((n) => !(noteEdits[n.id] ?? '').trim()) ? 'A note is corrected, never emptied'
                  : primaries > 1 ? 'Only one member can be the primary — make one a backup'
                  : !reason.trim() ? 'Say why — it goes on the record with the change'
                    : null

  const refs = {
    caller: useRef<HTMLDivElement>(null), where: useRef<HTMLDivElement>(null),
    what: useRef<HTMLDivElement>(null), times: useRef<HTMLDivElement>(null),
    end: useRef<HTMLDivElement>(null),
  }

  async function save() {
    if (needs || busy) return
    const fix: Record<string, string | null> = {}
    if (callInMoved && base && !dayOnly) fix.called_at = base
    if (!crewAccepted && !acceptedGone && changed(accepted)) fix.accepted_at = effective(accepted, base)
    if (!crewArrived && !arrivalGone && changed(onScene)) fix.on_scene_at = effective(onScene, base)
    if (changed(covered)) fix.covered_at = effective(covered, base)
    if (closedCall && changed(closed)) fix.closed_at = effective(closed, base)
    const details: Record<string, unknown> = {}
    const text = (k: keyof Words) => { if (differs(k)) details[k] = norm(w[k] as string) || null }
    if (!nameLocked) text('caller_name')
    if (!phoneLocked) text('caller_phone')
    for (const k of ['address', 'location_details', 'cross_street_a', 'cross_street_b', 'location_note'] as const) text(k)
    if (relChanged) {
      details.relationship_id = w.relationship_id || null
      details.relationship_label = norm(w.relationship_label) || null
    }
    for (const k of ['caller_on_scene', 'caller_urgency', 'anonymous', 'priority'] as const) {
      if (differs(k)) details[k] = w[k]
    }
    if (closedCall && resChanged) {
      details.resolution_id = w.resolution_id || null
      details.resolution_label = norm(w.resolution_label) || null
    }
    if (closedCall && differs('outcome_note')) details.outcome_note = w.outcome_note.trim() || null
    setBusy(true)
    setError(null)
    try {
      const r = await api.incidentCorrect(call.id, {
        reason: reason.trim(),
        call: fix,
        responders: onCall.filter((r) => changed(r.took) || changed(r.arrived)).map((r) => ({
          id: r.id,
          ...(changed(r.took) ? { accepted_at: effective(r.took, base) } : {}),
          ...(changed(r.arrived) ? { on_scene_at: effective(r.arrived, base) } : {}),
        })),
        added: added.map((a) => ({
          member_id: a.id, role: a.role,
          accepted_at: a.took ? placeTime(a.took, base) : null,
          on_scene_at: a.arrived ? placeTime(a.arrived, base) : null,
        })),
        remove: rows.filter((r) => r.off).map((r) => r.id),
        details,
        answers: answerChanges.length
          ? Object.fromEntries(answerChanges.map((q) => [q.key, answers[q.key] ?? ''])) : undefined,
        natures: naturesChanged ? {
          first: firstId && firstId !== (callNatures[0]?.nature_id ?? '') ? firstId : null,
          add: extras.filter((e) => e.position === null && !e.removed).map((e) => e.nature_id),
          remove: extras.filter((e) => e.position !== null && e.removed).map((e) => e.position as number),
        } : undefined,
        numbers: numChanges.length || numAdds.length ? {
          change: numChanges.map((n) => ({ id: n.id, label: norm(n.label) || null, phone: n.phone.trim() })),
          add: numAdds.map((n) => ({ label: norm(n.label) || null, phone: n.phone.trim() })),
        } : undefined,
        notes: noteChanges.length
          ? { change: noteChanges.map((n) => ({ id: n.id, body: noteEdits[n.id] ?? n.body })) }
          : undefined,
        fingerprint: call.fingerprint ?? undefined,
        called_day: callInMoved && dayOnly ? date : undefined,
      })
      onSaved(r)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the corrections.')
    } finally {
      setBusy(false)
    }
  }

  const setRow = (i: number, patch: Partial<(typeof rows)[number]>) =>
    setRows((all) => all.map((r, at) => (at === i ? { ...r, ...patch } : r)))
  const jump = (key: keyof typeof refs) => refs[key].current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  const chips = <T,>(options: { v: T; label: string }[], value: T, onPick: (v: T) => void, allowNone = false) => (
    <div className="chips">
      {options.map((o) => (
        <button type="button" key={String(o.v)} className={`chip ${value === o.v ? 'on' : ''}`}
                onClick={() => onPick(allowNone && value === o.v ? (null as T) : o.v)}>
          {o.label}
        </button>
      ))}
    </div>
  )

  return (
    <div className="box ccor" style={{ marginTop: 15 }}>
      <h4>
        Correct this call
        {total > 0 && <span className="pill ccor-ed">{total} change{total === 1 ? '' : 's'} not saved yet</span>}
      </h4>
      <div className="bd">
        <p className="ccor-say">
          Change anything that was wrong. Every change keeps the old value, your name and the
          reason, and Reports use the corrected call. Nothing is sent to anybody.
        </p>
        <div className="ccor-jump">
          {([['caller', 'Caller', callerCount], ['where', 'Where', whereCount], ['what', 'What', whatCount],
            ['times', 'Times & who went', timeCount], ...(closedCall ? [['end', 'How it ended', endCount]] : [])] as
            [keyof typeof refs, string, number][]).map(([key, label, n]) => (
              <button type="button" key={key} className={n ? 'n' : ''} onClick={() => jump(key)}>
                {label}{n ? ` · ${n}` : ''}
              </button>
          ))}
        </div>
        {error && <div className="notice">{error}</div>}

        {/* ---- Caller ---- */}
        <div className="ccor-sec" ref={refs.caller}>
          <div className="ccor-h">Caller</div>
          <div className="ccor-g2">
            <Row label="Caller name" changed={!nameLocked && differs('caller_name')} was={start.caller_name}
                 locked={nameLocked ? 'from the phone system — stays as it came' : undefined}>
              <input value={w.caller_name} maxLength={200} disabled={nameLocked}
                     onChange={(e) => set({ caller_name: e.target.value })} />
            </Row>
            <Row label="Called from" changed={!phoneLocked && differs('caller_phone')} was={start.caller_phone}
                 locked={phoneLocked ? 'from the phone system — stays as it came' : undefined}>
              <PhoneInput value={w.caller_phone} maxLength={40} disabled={phoneLocked} aria-label="Called from"
                          onChange={(e) => set({ caller_phone: e.target.value })} />
            </Row>
          </div>
          <div className="field ccor-f">
            <label>Other numbers</label>
            {nums.map((n, i) => {
              const was = (call.callback_numbers ?? [])[i]
              const chg = !!was && (norm(n.label) !== norm(was.label) || norm(n.phone) !== norm(was.phone_raw))
              return (
                <div className={`othernumber ${chg ? 'chg' : ''}`} key={n.id}>
                  <input value={n.label} maxLength={40} placeholder="Home, wife…" aria-label="Whose number"
                         onChange={(e) => setNums((all) => all.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                  <PhoneInput value={n.phone} maxLength={40} aria-label="Number"
                              onChange={(e) => setNums((all) => all.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))} />
                  {chg && was && <span className="ccor-wasnote">was {was.label ?? 'no note'} {was.phone_raw}</span>}
                </div>
              )
            })}
            {newNums.map((n, i) => (
              <div className="othernumber chg" key={`new-${i}`}>
                <input value={n.label} maxLength={40} placeholder="Home, wife…" aria-label="Whose number"
                       onChange={(e) => setNewNums((all) => all.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                <PhoneInput value={n.phone} maxLength={40} placeholder="Number" aria-label="Number"
                            onChange={(e) => setNewNums((all) => all.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))} />
                <button type="button" className="ghost tiny" aria-label="Take this number off the list"
                        onClick={() => setNewNums((all) => all.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            {nums.length + newNums.length < 10 && (
              <button type="button" className="ghost tiny" onClick={() => setNewNums((all) => [...all, { label: '', phone: '' }])}>
                + Add another number
              </button>
            )}
          </div>
          <div className="ccor-g2">
            <Row label="Calling for" changed={relChanged} was={start.relationship_label}>
              {chips(relationships.filter((r) => !r.retired_at).map((r) => ({ v: r.id, label: r.name })),
                w.relationship_id || relationships.find((r) => r.name === w.relationship_label)?.id || '',
                (v) => set({ relationship_id: v ?? '', relationship_label: relationships.find((r) => r.id === v)?.name ?? '' }), true)}
            </Row>
            <Row label="Is the caller staying on scene?" changed={differs('caller_on_scene')}
                 was={STAYING[String(start.caller_on_scene)]}>
              {chips([{ v: true as boolean | null, label: 'Staying' }, { v: false as boolean | null, label: 'Not staying' }],
                w.caller_on_scene, (v) => set({ caller_on_scene: v }), true)}
            </Row>
          </div>
          <div className="ccor-g2">
            <Row label="Caller urgency" changed={differs('caller_urgency')}
                 was={start.caller_urgency ? URGENCY[start.caller_urgency] : 'not said'}>
              {chips(PRIORITIES.map((p) => ({ v: p.v as number | null, label: p.label })),
                w.caller_urgency, (v) => set({ caller_urgency: v }), true)}
            </Row>
            <Row label="Anonymous" changed={differs('anonymous')} was={start.anonymous ? 'yes' : 'no'}>
              <label className="ccor-tick">
                <input type="checkbox" checked={w.anonymous} onChange={(e) => set({ anonymous: e.target.checked })} />
                <span>The caller asked not to be identified</span>
              </label>
            </Row>
          </div>
          {/* THE NOTES (081), each in its own colours: a typo fixed here, and
              what it said is kept with the correction. */}
          {callNotes.length > 0 && (
            <>
              <div className="ccor-h">Notes</div>
              <div className="cn-pair">
                {callNotes.map((n) => (
                  <NoteEditor key={n.id} note={n} value={noteEdits[n.id] ?? n.body}
                              onChange={(text) => setNoteEdits((e) => ({ ...e, [n.id]: text }))} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* ---- Where ---- */}
        <div className="ccor-sec" ref={refs.where}>
          <div className="ccor-h">Where</div>
          <div className="ccor-g2">
            <Row label="Address" changed={differs('address')} was={start.address}>
              <input value={w.address} maxLength={400} onChange={(e) => set({ address: e.target.value })} />
            </Row>
            <Row label="Location details" changed={differs('location_details')} was={start.location_details}>
              <input value={w.location_details} maxLength={400} placeholder="I-87 northbound, mile marker 23"
                     onChange={(e) => set({ location_details: e.target.value })} />
            </Row>
          </div>
          <div className="ccor-g3">
            <Row label="Between" changed={differs('cross_street_a')} was={start.cross_street_a}>
              <input value={w.cross_street_a} maxLength={120} onChange={(e) => set({ cross_street_a: e.target.value })} />
            </Row>
            <Row label="and" changed={differs('cross_street_b')} was={start.cross_street_b}>
              <input value={w.cross_street_b} maxLength={120} onChange={(e) => set({ cross_street_b: e.target.value })} />
            </Row>
            <Row label="Directions" changed={differs('location_note')} was={start.location_note}>
              <input value={w.location_note} maxLength={400} onChange={(e) => set({ location_note: e.target.value })} />
            </Row>
          </div>
        </div>

        {/* ---- What ---- */}
        <div className="ccor-sec" ref={refs.what}>
          <div className="ccor-h">What</div>
          <Row label="Nature" changed={naturesChanged} was={call.nature_label}>
            <div className="ccor-nat">
              <select value={firstId} aria-label="The nature of call"
                      onChange={(e) => setFirstId(e.target.value)}>
                {!callNatures[0]?.nature_id && <option value="">{call.nature_label} (typed)</option>}
                {liveNatures
                  .filter((n) => n.id === firstId || !extras.some((x) => !x.removed && x.nature_id === n.id))
                  .map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
              </select>
              {extras.map((x, i) => (
                <span key={`${x.nature_id}-${i}`} className={`ccor-natx ${x.removed ? 'off' : ''} ${x.position === null ? 'new' : ''}`}>
                  + {x.label}
                  <button type="button" className="ghost tiny"
                          aria-label={x.removed ? `Put ${x.label} back` : `Take ${x.label} off`}
                          onClick={() => setExtras((all) => (x.position === null
                            ? all.filter((_, j) => j !== i)
                            : all.map((o, j) => (j === i ? { ...o, removed: !o.removed } : o))))}>
                    {x.removed ? 'undo' : '✕'}
                  </button>
                </span>
              ))}
              {firstId && (
                <select value="" aria-label="Add another nature"
                        onChange={(e) => {
                          const n = liveNatures.find((o) => o.id === e.target.value)
                          if (n) setExtras((all) => [...all, { position: null, nature_id: n.id, label: n.name, removed: false }])
                        }}>
                  <option value="">+ Add another nature</option>
                  {liveNatures.filter((n) => !natureIds.includes(n.id))
                    .map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
              )}
            </div>
          </Row>
          <Row label="Urgency" changed={differs('priority')} was={URGENCY[start.priority] ?? ''}>
            {chips(PRIORITIES.map((p) => ({ v: p.v, label: p.label })), w.priority, (v) => set({ priority: v }))}
          </Row>
          <div className="field ccor-f">
            <label>Answers{answerChanges.length ? <span className="ccor-wasnote">{answerChanges.length} corrected</span> : null}</label>
            {!tree ? <div className="dim">Loading the questions…</div>
              : tree.length === 0 ? <div className="dim">This nature asks no questions.</div>
                : (
                  <div className="qgrid">
                    <QuestionFields
                      tree={tree}
                      answers={answers}
                      alertNote={() => ''}
                      onChange={setAnswers}
                      sections={sectionNames}
                      asking={{ callerLeaving: w.caller_on_scene === false }}
                    />
                  </div>
                )}
          </div>
        </div>

        {/* ---- Times & who went (078) ---- */}
        <div className="ccor-sec" ref={refs.times}>
          <div className="ccor-h">Times &amp; who went</div>
          <dl className="ccor-kv">
            <dt>Call in</dt>
            <dd className="ccor-when">
              <input type="date" value={date} aria-label="The day it came in"
                     onChange={(e) => setDate(e.target.value)} />
              <input type="time" value={time} aria-label="The time it came in"
                     onChange={(e) => setTime(e.target.value)} />
              {calledUnknown && !time && <small className="lgc-unknown">time not known</small>}
            </dd>
            <dt>Broadcast</dt>
            <dd className="dim">{clockOf(call.broadcast_at) || 'not sent'} — what the system did; not corrected</dd>
            <dt>Accepted</dt>
            <dd>
              {acceptedGone
                ? <span className="dim">{nobodyGoing
                    ? 'none — nobody is left going, so the call is back to waiting'
                    : "none — it was the member's time, and he is taken off"}</span>
                : firstAccepted
                  ? <span className="dim">{clockOf(firstAccepted)} — the first member to take it; change it on his line</span>
                  : <ClockField value={accepted.text} base={base} label="When it was accepted"
                              kept={effective(accepted, base)}
                              onChange={(text) => setAccepted({ ...accepted, text })} />}
            </dd>
            <dt>On scene</dt>
            <dd>
              {firstArrived
                ? <span className="dim">{clockOf(firstArrived)} — the first member to arrive; change it on his line</span>
                : arrivalGone
                  ? <span className="dim">none — it was the member's arrival, taken away on his line</span>
                  : <ClockField value={onScene.text} base={base} label="When help got there"
                                kept={effective(onScene, base)}
                                onChange={(text) => setOnScene({ ...onScene, text })} />}
            </dd>
            <dt>Covered</dt>
            <dd><ClockField value={covered.text} base={base} label="When it was covered"
                            kept={effective(covered, base)}
                            onChange={(text) => setCovered({ ...covered, text })} /></dd>
            {closedCall && (
              <>
                <dt>Closed</dt>
                <dd><ClockField value={closed.text} base={base} label="When it closed"
                                required={!closedUnknown}
                                kept={effective(closed, base)}
                                onChange={(text) => setClosed({ ...closed, text })} />
                  {closedUnknown && !closed.text && <small className="lgc-unknown">time not known</small>}</dd>
              </>
            )}
          </dl>
          {rows.length + added.length + gone.length > 0 && (
            <table className="lgc-who" style={{ marginTop: 8 }}>
              <thead><tr><th>Member</th><th>Role</th><th>Took it</th><th>Arrived</th><th /></tr></thead>
              <tbody>
                {rows.map((r, i) => r.off ? (
                  /* TAKEN OFF ON THIS FORM (080): saved with the corrections
                     and the one Why; undo puts him back until then. */
                  <tr key={r.id} className="ccor-off">
                    <td className="lgc-nm"><s>{r.who}</s></td>
                    <td className="dim"><s>{r.role}</s></td>
                    <td className="dim"><s>{r.took.orig ? clockOf(r.took.orig) : 'not recorded'}</s></td>
                    <td className="dim"><s>{r.arrived.orig ? clockOf(r.arrived.orig) : '—'}</s></td>
                    <td>
                      <span className="pill rmv-pill">removed</span>{' '}
                      <button type="button" className="ghost tiny"
                              onClick={() => setRow(i, { off: false })}>undo</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={r.id}>
                    <td className="lgc-nm">{r.who}</td>
                    <td className="dim">{r.role}</td>
                    <td><ClockField value={r.took.text} base={base} label={`When ${r.who} took it`}
                                    required={!!r.took.orig}
                                    kept={effective(r.took, base)}
                                    onChange={(text) => setRow(i, { took: { ...r.took, text } })} />
                      {!r.took.orig && !r.took.text && <small className="lgc-unknown">not recorded</small>}</td>
                    <td><ClockField value={r.arrived.text} base={base} label={`When ${r.who} arrived`}
                                    kept={effective(r.arrived, base)}
                                    onChange={(text) => setRow(i, { arrived: { ...r.arrived, text } })} /></td>
                    <td>
                      {(changed(r.took) || changed(r.arrived)) && <span className="pill ccor-ed">changed</span>}{' '}
                      <button type="button" className="btn sm rmv-btn" aria-label={`Take ${r.who} off this call`}
                              onClick={() => setRow(i, { off: true })}>Remove</button>
                    </td>
                  </tr>
                ))}
                {gone.map((r) => (
                  <tr key={r.id} className="ccor-off">
                    <td className="lgc-nm"><s>{r.who}</s></td>
                    <td className="dim" colSpan={3}>
                      removed {clockOf(r.removed_at!)} by {r.removed_by_label ?? 'a dispatcher'}
                      {' — '}{r.removed_reason ?? 'logged by mistake'}
                    </td>
                    <td><span className="pill rmv-pill">removed</span></td>
                  </tr>
                ))}
                {added.map((a, i) => (
                  <tr key={a.id}>
                    <td className="lgc-nm">{a.who}</td>
                    <td>
                      <select value={a.role} aria-label={`${a.who}'s role`}
                              onChange={(e) => setAdded((all) => all.map((o, at) => (at === i
                                ? { ...o, role: e.target.value as 'primary' | 'backup' } : o)))}>
                        <option value="backup">backup</option>
                        <option value="primary" disabled={hasPrimary && a.role !== 'primary'}>primary</option>
                      </select>
                    </td>
                    <td><ClockField value={a.took} base={base} label={`When ${a.who} took it`}
                                    onChange={(took) => setAdded((all) => all.map((o, at) => (at === i ? { ...o, took } : o)))} />
                      {!a.took && <small className="lgc-unknown">not known</small>}</td>
                    <td><ClockField value={a.arrived} base={base} label={`When ${a.who} arrived`}
                                    onChange={(arrived) => setAdded((all) => all.map((o, at) => (at === i ? { ...o, arrived } : o)))} /></td>
                    <td>
                      <span className="pill ccor-ed">added</span>{' '}
                      <button type="button" className="ghost tiny" aria-label={`Take ${a.who} off the list`}
                              onClick={() => setAdded((all) => all.filter((_, at) => at !== i))}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {promoted && (
            <p className="ccor-say">{promoted.who} becomes the primary.</p>
          )}
          <MemberPicker
            roster={roster}
            exclude={[...rows.map((r) => r.member_id), ...added.map((a) => a.id)]}
            label="Add a member who went"
            placeholder="+ Add a member who went and was never logged — a unit, a name, or the last digits"
            onPick={(id, who) => setAdded((all) => [...all, {
              id, who, role: hasPrimary ? 'backup' : 'primary', took: '', arrived: '',
            }])}
          />
        </div>

        {/* ---- How it ended (a closed call) ---- */}
        {closedCall && (
          <div className="ccor-sec" ref={refs.end}>
            <div className="ccor-h">How it ended</div>
            <Row label="Outcome" changed={resChanged} was={start.resolution_label}>
              {chips(resolutions.filter((r) => !r.retired_at).map((r) => ({ v: r.name, label: r.name })),
                w.resolution_label, (v) => set({
                  resolution_label: v ?? '', resolution_id: resolutions.find((r) => r.name === v)?.id ?? '',
                }))}
            </Row>
            <Row label="Closing note" changed={differs('outcome_note')} was={start.outcome_note}>
              <textarea rows={2} maxLength={4000} value={w.outcome_note}
                        onChange={(e) => set({ outcome_note: e.target.value })} />
            </Row>
          </div>
        )}

        <div className="ccor-sec ccor-stays">
          What the system did stays as it was: the broadcast and its time, every message sent and to
          whom, the history, and the call number {call.number}.
        </div>

        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="ccor-why">Why — it goes on the record with the change</label>
          <input id="ccor-why" value={reason} maxLength={400}
                 placeholder="Typed in a hurry — corrected from the paper log"
                 onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="ccor-bar">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <span className="sep" />
          {needs && <span className="ineed">{needs}</span>}
          <button type="button" className="btn primary" disabled={!!needs || busy} onClick={save}>
            {busy ? 'Saving…' : 'Save the corrections'}
          </button>
        </div>
      </div>
    </div>
  )
}
